import styles from './BarList.module.css';
import { useI18n } from '../../i18n/I18nContext.js';

export interface BarListItem {
  key: string;
  label: string;
  value: number;
  /** Formatted for display; the raw `value` only drives bar width and sort. */
  displayValue: string;
  color?: string;
}

export interface BarListProps {
  items: BarListItem[];
  /** Shown once when every item shares the same color (a single series needs no legend box). */
  seriesLabel?: string;
  emptyLabel: string;
}

/**
 * Ranked horizontal bars. Each bar is capped at 20px thick with a fully
 * rounded end, grown from a shared baseline (the track's left edge), value
 * printed at the tip rather than crammed inside — the dataviz skill's
 * guidance for a label that must never be clipped.
 */
export function BarList({ items, seriesLabel, emptyLabel }: BarListProps) {
  const { t } = useI18n();
  if (items.length === 0) {
    return <p className={styles.empty}>{emptyLabel}</p>;
  }
  const max = Math.max(...items.map((i) => Math.abs(i.value)), 1);
  const distinctColors = new Set(items.map((i) => i.color ?? 'var(--chart-series-1)'));

  return (
    <div className={styles.root}>
      {distinctColors.size > 1 && (
        <div className={styles.legend}>
          {items.map((item) => (
            <span className={styles.legendItem} key={item.key}>
              <span className={styles.swatch} style={{ background: item.color ?? 'var(--chart-series-1)' }} />
              {item.label}
            </span>
          ))}
        </div>
      )}
      {distinctColors.size === 1 && seriesLabel && (
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={styles.swatch} style={{ background: items[0]?.color ?? 'var(--chart-series-1)' }} />
            {seriesLabel}
          </span>
        </div>
      )}
      {items.map((item) => (
        <div className={styles.row} key={item.key} title={`${item.label}: ${item.displayValue}`}>
          <span className={styles.rowLabel}>{item.label}</span>
          <span className={styles.track} role="img" aria-label={t('common.status')}>
            <span
              className={styles.fill}
              style={{
                width: `${Math.max((Math.abs(item.value) / max) * 100, 2)}%`,
                background: item.color ?? 'var(--chart-series-1)',
              }}
            />
          </span>
          <span className={`${styles.rowValue} numeric`}>{item.displayValue}</span>
        </div>
      ))}
    </div>
  );
}
